/**
 * ADM-17/18 筛选分班工作台（balanced-grouping:SC-01~SC-17 前端，placement-workbench-ui）。
 *
 * 严格对照定稿原型 `prototype/admin/src/pages/classes/PlacementWorkbenchPage.tsx`（2026-08-20 评审定稿）转译：
 * 五维筛选（科目必选；分数段档位按所选科目满分动态派生、切换科目清空已选档）+ 统计行 +
 * 规则提示 + 分组卡片（默认全选；勾选同时决定换班对象与确认入班名单）+ 溢出警示 + 空态；
 * 组内调整「添加（联想搜索多选/跨组移出）/移除（Popconfirm 二次确认）/换班（组头勾选或行内单生，对话框罗列姓名+性别）」纯前端状态不落库；
 * 确认对话框（preview 预填服务端自动四要素 + 已勾选学生姓名（性别）标签罗列 + 可改后按勾选提交 confirm）；
 * 同页结果（已确认/部分失败/逐生明细/重新确认补入失败学生）+ 重新查询重置。
 * 组内调整与确认结果均为前端建议状态；失败学生补入走既有单生端点（POST /classes/:id/members）。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DraggableModal } from "../../components/DraggableModal";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  placementsApi,
  type PlacementCandidate,
  type PlacementConfirmItem,
  type PlacementPreview,
  type PlacementSearchStudent,
} from "../../api/placements.api";
import { classesApi } from "../../api/classes.api";
import { subjectsApi, type SubjectItem } from "../../api/subjects.api";
import { orgCampusApi, type CampusItem } from "../../api/campuses.api";
import { staffApi, type StaffListItem } from "../../api/staff.api";
import { classroomsApi, type ClassroomItem } from "../../api/classrooms.api";
import { genderLabel, gradeStageLabel } from "../../api/labels";
import { ApiError } from "../../api/transport";
import { HasPermission } from '../../auth/HasPermission';

const { Title, Text } = Typography;

/**
 * 分数段选择值（归一状态，subject-score-bands-and-scores 协议硬切）：
 * 档位区间（来自科目 derivedScoreBands 快捷选择）| 未录入 | 自定义区间（输入中可空）。
 * 提交时统一直传 {scoreMin, scoreMax} 或 {scoreNone}，不再有档位码字段。
 */
type ScoreFilter =
  | { kind: "band"; min: number; max: number }
  | { kind: "none" }
  | { kind: "custom"; min: number | null; max: number | null };

/** 自定义区间合法性（0~1000 整数且下界不大于上界；输入中空值视为非法）。 */
function isCustomRangeValid(min: number | null, max: number | null): boolean {
  return (
    min !== null &&
    max !== null &&
    Number.isInteger(min) &&
    Number.isInteger(max) &&
    min >= 0 &&
    max <= 1000 &&
    min <= max
  );
}

/** 单个边界是否为合法的 0-1000 整数。 */
function isBoundValid(v: number | null): v is number {
  return v !== null && Number.isInteger(v) && v >= 0 && v <= 1000;
}

/**
 * 自定义区间行内错误文案（归因到具体输入框；合法或无涉错误返回 null）：
 * 非法整数/空值提示在所属输入框下；下界大于上界提示在下界输入框下。
 */
function customRangeFieldError(
  field: "min" | "max",
  min: number | null,
  max: number | null,
): string | null {
  const own = field === "min" ? min : max;
  if (own === null) return "请输入分数";
  if (!isBoundValid(own)) return "分数必须为 0-1000 的整数";
  if (field === "min" && isBoundValid(max) && own > max) {
    return "下界不能大于上界";
  }
  return null;
}

/** 前端建议分组（组内调整的纯前端状态）。 */
interface SuggestedGroup {
  index: number;
  skuId: string;
  skuName: string;
  capacity: number;
  classId?: string;
  confirmed: boolean;
  students: PlacementCandidate[];
}

/** 提取错误信息文案（错误信封中文 message）。 */
function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

/**
 * 员工是否可作为该校区教师。
 * 列表契约以 campusGrants.campusId 为准；仅有 campusNames 时按名称兜底；
 * 两者皆无时不过滤，避免选项被滤空后 Select 回显教师 UUID。
 */
function staffServesCampus(
  staff: StaffListItem,
  campusId: string | undefined,
  campusName: string,
): boolean {
  if (staff.status !== "ACTIVE") return false;
  if (!campusId) return true;
  const grants = staff.campusGrants ?? [];
  if (grants.length > 0) {
    return grants.some((g) => g.campusId === campusId);
  }
  const names = staff.campusNames ?? [];
  if (names.length > 0 && campusName) {
    return names.includes(campusName);
  }
  return true;
}

/** 是否存在可换入的其他未确认分组（仅一组或其余已确认时不展示换班）。 */
function hasUnconfirmedMoveTarget(
  groups: SuggestedGroup[],
  fromIndex: number,
): boolean {
  return groups.some((g) => g.index !== fromIndex && !g.confirmed);
}

/** 本组当前勾选且仍在名单中的学生（确认入班与组头换班共用）。 */
function pickedStudentsOf(
  group: SuggestedGroup,
  selectedIds: string[] | undefined,
): PlacementCandidate[] {
  const ids = new Set(selectedIds ?? []);
  return group.students.filter((s) => ids.has(s.studentId));
}

export function PlacementWorkbenchPage() {
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [campusId, setCampusId] = useState<string | undefined>();
  const [gradeStage, setGradeStage] = useState<string | undefined>();
  const [subjectId, setSubjectId] = useState<string | undefined>();
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter | undefined>();
  const [gender, setGender] = useState<string | undefined>();
  const [queried, setQueried] = useState(false);
  const [loading, setLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [groups, setGroups] = useState<SuggestedGroup[]>([]);
  const [overflow, setOverflow] = useState<PlacementCandidate[]>([]);
  const [notices, setNotices] = useState<string[]>([]);
  const [skuSummaries, setSkuSummaries] = useState<
    Array<{
      skuId: string;
      skuName: string;
      candidateCount: number;
      suggestedClassCount: number;
      maxClassCapacity: number;
      maxClassCount: number;
    }>
  >([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [groupResults, setGroupResults] = useState<
    Record<number, PlacementConfirmItem[]>
  >({});
  const [selected, setSelected] = useState<Record<number, string[]>>({});
  const [confirming, setConfirming] = useState<{
    group: SuggestedGroup;
    preview: PlacementPreview;
  } | null>(null);
  const [teachers, setTeachers] = useState<StaffListItem[]>([]);
  const [classrooms, setClassrooms] = useState<ClassroomItem[]>([]);
  const [addingIndex, setAddingIndex] = useState<number | null>(null);
  const [addKeyword, setAddKeyword] = useState("");
  const [addCandidates, setAddCandidates] = useState<PlacementSearchStudent[]>(
    [],
  );
  const [addPicked, setAddPicked] = useState<string[]>([]);
  const [addSearching, setAddSearching] = useState(false);
  const [movingIndex, setMovingIndex] = useState<number | null>(null);
  const [moveStudentIds, setMoveStudentIds] = useState<string[]>([]);
  const [moveTarget, setMoveTarget] = useState<number | null>(null);
  const [form] = Form.useForm<{
    className: string;
    teacherId: string;
    classroomId: string;
    dateRange: [Dayjs, Dayjs];
  }>();

  useEffect(() => {
    void (async () => {
      try {
        const [campusPage, subjectPage] = await Promise.all([
          orgCampusApi.listCampuses({ pageSize: 100 }),
          subjectsApi.listSubjects({ pageSize: 100 }),
        ]);
        setCampuses(campusPage.items);
        setSubjects(subjectPage.items);
      } catch (err) {
        message.error(errorMessage(err));
      }
    })();
  }, []);

  /** 所选科目的派生档位（服务端权威派生，前端不自派生，SC-17）。 */
  const targetSubject = subjects.find((s) => s.id === subjectId);

  /** 分数段下拉选中值序列化（档位带区间、NONE/CUSTOM 为固定码）。 */
  const scoreFilterValue = useMemo(() => {
    if (!scoreFilter) return undefined;
    if (scoreFilter.kind === "band")
      return `band:${scoreFilter.min}-${scoreFilter.max}`;
    if (scoreFilter.kind === "none") return "NONE";
    return "CUSTOM";
  }, [scoreFilter]);

  /** 自定义区间行内错误（输入处常显；非法时不允许查询）。 */
  const customMinError =
    scoreFilter?.kind === "custom"
      ? customRangeFieldError("min", scoreFilter.min, scoreFilter.max)
      : null;
  const customMaxError =
    scoreFilter?.kind === "custom"
      ? customRangeFieldError("max", scoreFilter.min, scoreFilter.max)
      : null;

  /** 查询：科目必选（SC-01），自定义区间非法就地拦截不发请求（SC-18），服务端错误信封就地展示（SC-03）。 */
  const runQuery = useCallback(async () => {
    if (!subjectId) {
      message.error("请选择科目");
      return;
    }
    if (
      scoreFilter?.kind === "custom" &&
      !isCustomRangeValid(scoreFilter.min, scoreFilter.max)
    ) {
      // 非法/未填全：就地拦截不发请求（行内红字已在输入处常显，SC-18）
      return;
    }
    setLoading(true);
    setQueryError(null);
    try {
      const result = await placementsApi.getSuggestions({
        campusId,
        subjectId,
        gradeStage,
        ...(scoreFilter?.kind === "band"
          ? { scoreMin: scoreFilter.min, scoreMax: scoreFilter.max }
          : scoreFilter?.kind === "none"
            ? { scoreNone: true }
            : scoreFilter?.kind === "custom"
              ? {
                  scoreMin: scoreFilter.min ?? undefined,
                  scoreMax: scoreFilter.max ?? undefined,
                }
              : {}),
        gender,
      });
      setGroups(
        result.groups.map((g) => ({
          index: g.index,
          skuId: g.skuId,
          skuName: g.skuName,
          capacity: g.capacity,
          confirmed: false,
          students: g.students,
        })),
      );
      setOverflow(result.overflowStudents);
      setNotices(result.notices);
      setSkuSummaries(result.skuSummaries);
      setTotalCandidates(result.totalCandidates);
      const defaults: Record<number, string[]> = {};
      for (const g of result.groups) {
        defaults[g.index] = g.students.map((s) => s.studentId);
      }
      setSelected(defaults);
      setGroupResults({});
      setQueried(true);
    } catch (err) {
      setGroups([]);
      setOverflow([]);
      setQueried(false);
      setQueryError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [campusId, subjectId, gradeStage, scoreFilter, gender]);

  /**
   * 打开确认对话框：先调 preview 由服务端自动生成建班四要素（SC-11/SC-12）。
   * 仅携带当前勾选学生；组空/未勾选就地提示；预览失败（如无可派教师 409）展示错误信封且不弹窗。
   */
  const openConfirmDialog = async (group: SuggestedGroup) => {
    const current =
      groups.find((g) => g.index === group.index) ?? group;
    if (current.students.length === 0) {
      message.error("该组已无学生");
      return;
    }
    const picked = pickedStudentsOf(current, selected[group.index]);
    if (picked.length === 0) {
      message.error("请先勾选要入班的学生");
      return;
    }
    const campusForConfirm = campusId ?? picked[0]!.homeCampusId;
    try {
      const preview = await placementsApi.previewPlacement({
        skuId: group.skuId,
        campusId: campusForConfirm,
        studentIds: picked.map((s) => s.studentId),
      });
      // 教师选项：该校区教师岗位在职员工（按授权 campusId，与 preview/原型一致）
      const [staffPage, roomPage] = await Promise.all([
        staffApi.listStaffs({ position: "TEACHER", pageSize: 100 }),
        classroomsApi.listClassrooms({
          campusId: campusForConfirm,
          status: "AVAILABLE",
          pageSize: 100,
        }),
      ]);
      const campusName =
        campuses.find((c) => c.id === campusForConfirm)?.name ?? "";
      setTeachers(
        staffPage.items.filter((t) =>
          staffServesCampus(t, campusForConfirm, campusName),
        ),
      );
      setClassrooms(roomPage.items);
      setConfirming({ group, preview });
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  useEffect(() => {
    if (confirming) {
      form.setFieldsValue({
        className: confirming.preview.className,
        teacherId: confirming.preview.teacherId,
        classroomId: confirming.preview.defaultClassroomId,
        dateRange: [
          dayjs(confirming.preview.startDate),
          dayjs(confirming.preview.endDate),
        ],
      });
    }
  }, [confirming, form]);

  /** 提交确认：按教务修改后的最终值提交（studentIds 仅为当前勾选学生）。 */
  const submitConfirm = async () => {
    if (!confirming) return;
    const groupIndex = confirming.group.index;
    try {
      const values = await form.validateFields();
      const current =
        groups.find((g) => g.index === groupIndex) ?? confirming.group;
      if (current.students.length === 0) {
        message.error("该组已无学生");
        return;
      }
      const picked = pickedStudentsOf(current, selected[groupIndex]);
      if (picked.length === 0) {
        message.error("请先勾选要入班的学生");
        return;
      }
      const campusForConfirm = campusId ?? picked[0]!.homeCampusId;
      const result = await placementsApi.confirmPlacement({
        skuId: confirming.group.skuId,
        campusId: campusForConfirm,
        className: values.className,
        teacherId: values.teacherId,
        defaultClassroomId: values.classroomId,
        startDate: values.dateRange[0]!.format("YYYY-MM-DD"),
        endDate: values.dateRange[1]!.format("YYYY-MM-DD"),
        studentIds: picked.map((s) => s.studentId),
      });
      const failed = result.results.filter((r) => !r.success);
      if (failed.length > 0) {
        message.warning(
          `已确认，${result.results.length - failed.length} 人入班，${failed.length} 人失败`,
        );
      } else {
        message.success(
          `「${result.className}」已创建，${result.results.length} 名学生入班`,
        );
      }
      setGroupResults((prev) => ({ ...prev, [groupIndex]: result.results }));
      setGroups((prev) =>
        prev.map((g) =>
          g.index === groupIndex
            ? { ...g, confirmed: true, classId: result.classId }
            : g,
        ),
      );
      setConfirming(null);
      form.resetFields();
    } catch (err) {
      if (err instanceof ApiError) message.error(err.message);
    }
  };

  /** 重新确认：失败学生补入本组已建班级（不重复建班，走既有单生端点）。 */
  const retryGroup = async (group: SuggestedGroup) => {
    if (!group.classId) return;
    const prevResults = groupResults[group.index] ?? [];
    const failedIds = prevResults
      .filter((r) => !r.success)
      .map((r) => r.studentId);
    if (failedIds.length === 0) return;
    const next: PlacementConfirmItem[] = [];
    for (const studentId of failedIds) {
      try {
        await classesApi.enrollMember(group.classId, studentId);
        next.push({ studentId, success: true });
      } catch (err) {
        next.push({ studentId, success: false, reason: errorMessage(err) });
      }
    }
    const merged = prevResults.map(
      (r) => next.find((n) => n.studentId === r.studentId) ?? r,
    );
    const stillFailed = merged.filter((r) => !r.success).length;
    if (stillFailed > 0) {
      message.warning(`重新确认完成，${stillFailed} 人仍失败`);
    } else {
      message.success("重新确认完成，本组学生已全部入班");
    }
    setGroupResults((prev) => ({ ...prev, [group.index]: merged }));
  };

  /** 添加对话框联想搜索（输入即查，仅该科目剩余课时学生）。 */
  useEffect(() => {
    if (addingIndex === null || !subjectId) {
      setAddCandidates([]);
      return;
    }
    const keyword = addKeyword.trim();
    if (!keyword) {
      setAddCandidates([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        setAddSearching(true);
        try {
          const results = await placementsApi.searchStudents({
            subjectId,
            campusId,
            keyword,
            limit: 20,
          });
          setAddCandidates(results);
        } catch {
          setAddCandidates([]);
        } finally {
          setAddSearching(false);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [addKeyword, addingIndex, subjectId, campusId]);

  const closeAddDialog = () => {
    setAddingIndex(null);
    setAddKeyword("");
    setAddPicked([]);
    setAddCandidates([]);
  };

  /** 提交添加：多选一次加多名；已在其他分组的学生自动移出原分组；容量超限拒绝。 */
  const submitAdd = () => {
    if (addingIndex === null) return;
    const group = groups.find((g) => g.index === addingIndex);
    if (!group) return;
    if (addPicked.length === 0) {
      message.error("请选择要添加的学生");
      return;
    }
    if (group.students.length + addPicked.length > group.capacity) {
      message.error(
        `添加后将超过每班最多人数（${group.capacity} 人），可先移除或换班腾出容量`,
      );
      return;
    }
    const movedFromOther = addPicked.filter((id) =>
      groups.some(
        (g) =>
          g.index !== addingIndex && g.students.some((s) => s.studentId === id),
      ),
    );
    const pickedStudents: PlacementCandidate[] = addCandidates
      .filter((s) => addPicked.includes(s.studentId))
      .map((s) => ({
        studentId: s.studentId,
        studentNo: s.studentNo,
        name: s.name,
        gender: s.gender,
        gradeStage: s.gradeStage,
        score: s.score,
        homeCampusId: campusId ?? "",
        targetSkuId: group.skuId,
        targetSkuName: group.skuName,
      }));
    setGroups((prev) =>
      prev.map((g) => {
        if (g.index === addingIndex) {
          return { ...g, students: [...g.students, ...pickedStudents] };
        }
        const remaining = g.students.filter(
          (s) => !addPicked.includes(s.studentId),
        );
        return remaining.length === g.students.length
          ? g
          : { ...g, students: remaining };
      }),
    );
    setSelected((prev) => {
      const next: Record<number, string[]> = { ...prev };
      for (const key of Object.keys(next)) {
        const idx = Number(key);
        if (idx !== addingIndex) {
          next[idx] = next[idx]!.filter((id) => !addPicked.includes(id));
        }
      }
      next[addingIndex] = [...(next[addingIndex] ?? []), ...addPicked];
      return next;
    });
    message.success(
      `已添加 ${addPicked.length} 名学生至建议${addingIndex}班` +
        (movedFromOther.length > 0
          ? `（其中 ${movedFromOther.length} 人自其他分组移入）`
          : ""),
    );
    closeAddDialog();
  };

  /** 从建议分组移除个别学生（仅确认前；重新查询会按规则重算）。 */
  const removeStudent = (group: SuggestedGroup, studentId: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.index === group.index
          ? {
              ...g,
              students: g.students.filter((s) => s.studentId !== studentId),
            }
          : g,
      ),
    );
    setSelected((prev) => ({
      ...prev,
      [group.index]: (prev[group.index] ?? []).filter((id) => id !== studentId),
    }));
    message.success("已从该分组移除，重新查询后将按规则重算");
  };

  const closeMoveDialog = () => {
    setMovingIndex(null);
    setMoveStudentIds([]);
    setMoveTarget(null);
  };

  /** 组头换班：以当前勾选学生为换班对象。 */
  const openMoveDialog = (group: SuggestedGroup) => {
    const ids = selected[group.index] ?? [];
    if (ids.length === 0) {
      message.error("请先勾选要换班的学生");
      return;
    }
    setMovingIndex(group.index);
    setMoveStudentIds(ids);
    setMoveTarget(null);
  };

  /** 行内换班：仅针对该学生，不依赖勾选。 */
  const openMoveDialogForStudent = (
    group: SuggestedGroup,
    studentId: string,
  ) => {
    setMovingIndex(group.index);
    setMoveStudentIds([studentId]);
    setMoveTarget(null);
  };

  /** 提交换班：对话框内学生移至目标分组（容量校验），移动后保持勾选。 */
  const submitMove = () => {
    if (movingIndex === null || moveTarget === null) return;
    const source = groups.find((g) => g.index === movingIndex);
    const target = groups.find((g) => g.index === moveTarget);
    if (!source || !target) return;
    const movedIds = moveStudentIds;
    if (movedIds.length === 0) {
      message.error("请先勾选要换班的学生");
      return;
    }
    if (target.students.length + movedIds.length > target.capacity) {
      message.error(`目标分组容量不足（每班最多 ${target.capacity} 人）`);
      return;
    }
    const movedStudents = source.students.filter((s) =>
      movedIds.includes(s.studentId),
    );
    setGroups((prev) =>
      prev.map((g) => {
        if (g.index === movingIndex) {
          return {
            ...g,
            students: g.students.filter((s) => !movedIds.includes(s.studentId)),
          };
        }
        if (g.index === moveTarget) {
          return { ...g, students: [...g.students, ...movedStudents] };
        }
        return g;
      }),
    );
    setSelected((prev) => ({
      ...prev,
      [movingIndex]: (prev[movingIndex] ?? []).filter(
        (id) => !movedIds.includes(id),
      ),
      [moveTarget]: [...(prev[moveTarget] ?? []), ...movedIds],
    }));
    message.success(`已将 ${movedIds.length} 名学生换至建议${moveTarget}班`);
    closeMoveDialog();
  };

  const groupColumns = (group: SuggestedGroup) => [
    {
      title: (
        <Checkbox
          checked={
            (selected[group.index] ?? []).length === group.students.length &&
            group.students.length > 0
          }
          indeterminate={
            (selected[group.index] ?? []).length > 0 &&
            (selected[group.index] ?? []).length < group.students.length
          }
          disabled={group.confirmed}
          onChange={(e) =>
            setSelected((prev) => ({
              ...prev,
              [group.index]: e.target.checked
                ? group.students.map((s) => s.studentId)
                : [],
            }))
          }
        />
      ),
      width: 40,
      render: (_: unknown, r: PlacementCandidate) => (
        <Checkbox
          checked={(selected[group.index] ?? []).includes(r.studentId)}
          disabled={group.confirmed}
          onChange={(e) =>
            setSelected((prev) => {
              const cur = prev[group.index] ?? [];
              return {
                ...prev,
                [group.index]: e.target.checked
                  ? [...cur, r.studentId]
                  : cur.filter((id) => id !== r.studentId),
              };
            })
          }
        />
      ),
    },
    { title: "学号", dataIndex: "studentNo" },
    { title: "姓名", dataIndex: "name" },
    {
      title: "性别",
      dataIndex: "gender",
      render: (v: string) => genderLabel[v] ?? v,
    },
    {
      title: "年级",
      dataIndex: "gradeStage",
      render: (v: string) =>
        gradeStageLabel[v as keyof typeof gradeStageLabel] ?? v,
    },
    {
      title: "分数",
      dataIndex: "score",
      render: (v: number | null) => (v !== null ? v : "未录入"),
    },
    ...(!group.confirmed
      ? [
          {
            title: "",
            dataIndex: "studentId",
            width: hasUnconfirmedMoveTarget(groups, group.index) ? 120 : 70,
            render: (_: string, r: PlacementCandidate) => (
              <Space size={0}>
                {hasUnconfirmedMoveTarget(groups, group.index) && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() =>
                      openMoveDialogForStudent(
                        groups.find((g) => g.index === group.index) ?? group,
                        r.studentId,
                      )
                    }
                  >
                    换班
                  </Button>
                )}
                <Popconfirm
                  title={`确认将「${r.name}」从该分组移除？`}
                  description="仅从本次建议分组移除，不写入班级；重新查询后将按规则重算。"
                  okText="移除"
                  cancelText="取消"
                  onConfirm={() =>
                    removeStudent(
                      groups.find((g) => g.index === group.index) ?? group,
                      r.studentId,
                    )
                  }
                >
                  <Button type="link" size="small" danger>
                    移除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
    ...(group.confirmed
      ? [
          {
            title: "结果",
            dataIndex: "studentId",
            render: (v: string) => {
              const res = (groupResults[group.index] ?? []).find(
                (x) => x.studentId === v,
              );
              if (!res) return <Tag>未入班</Tag>;
              return res.success ? (
                <Tag color="green">已入班</Tag>
              ) : (
                <Tag color="red">{res.reason ?? "失败"}</Tag>
              );
            },
          },
        ]
      : []),
  ];

  const dialogStudents = confirming
    ? pickedStudentsOf(
        groups.find((g) => g.index === confirming.group.index) ??
          confirming.group,
        selected[confirming.group.index],
      )
    : [];
  const movingStudents =
    movingIndex === null
      ? []
      : (groups.find((g) => g.index === movingIndex)?.students ?? []).filter(
          (s) => moveStudentIds.includes(s.studentId),
        );
  const teacherSelectOptions = useMemo(() => {
    const opts = teachers.map((t) => ({ label: t.name, value: t.id }));
    const preview = confirming?.preview;
    if (
      preview?.teacherId &&
      preview.teacherName &&
      !opts.some((o) => o.value === preview.teacherId)
    ) {
      opts.unshift({ label: preview.teacherName, value: preview.teacherId });
    }
    return opts;
  }, [teachers, confirming]);

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>筛选分班工作台</Title>
          <p className="page-header-desc">
            选择筛选条件后，系统按该科目 SKU
            配置的每班最多人数与最多班级数自动给出建议分组，逐组确认后创建班级并入班。
          </p>
        </div>
      </div>

      <div className="page-filter-card">
        <Space wrap size={12}>
          <Select
            placeholder="校区"
            allowClear
            style={{ width: 140 }}
            value={campusId}
            onChange={(v) => {
              setCampusId(v);
              setQueried(false);
            }}
            options={campuses.map((c) => ({ label: c.name, value: c.id }))}
          />
          <Select
            placeholder="科目"
            allowClear
            style={{ width: 140 }}
            value={subjectId}
            onChange={(v) => {
              setSubjectId(v);
              // 切换科目清空已选分数段（档位随科目派生变化，避免语义漂移，SC-17）
              setScoreFilter(undefined);
              setQueried(false);
            }}
            options={subjects.map((s) => ({ label: s.name, value: s.id }))}
          />
          <Select
            placeholder="年级/学段"
            allowClear
            style={{ width: 140 }}
            value={gradeStage}
            onChange={(v) => {
              setGradeStage(v);
              setQueried(false);
            }}
            options={Object.entries(gradeStageLabel).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select<string>
            placeholder="分数段"
            allowClear
            style={{ width: 170 }}
            value={scoreFilterValue}
            onChange={(v) => {
              if (v === "NONE") {
                setScoreFilter({ kind: "none" });
              } else if (v === "CUSTOM") {
                setScoreFilter({ kind: "custom", min: null, max: null });
              } else if (v?.startsWith("band:")) {
                const [min, max] = v.slice(5).split("-").map(Number);
                setScoreFilter({ kind: "band", min: min!, max: max! });
              } else {
                setScoreFilter(undefined);
              }
              setQueried(false);
            }}
            options={[
              // 档位选项 = 科目 derivedScoreBands 映射（服务端权威派生，一键填区间）
              ...(targetSubject?.derivedScoreBands ?? []).map((b) => ({
                label: b.label,
                value: `band:${b.min}-${b.max}`,
              })),
              { label: "未录入分数", value: "NONE" },
              { type: "divider" as const },
              {
                // 已填合法区间时回显「N-M 分（自定义）」
                label:
                  scoreFilter?.kind === "custom" &&
                  scoreFilter.min !== null &&
                  scoreFilter.max !== null
                    ? `${scoreFilter.min}-${scoreFilter.max} 分（自定义）`
                    : "自定义区间…",
                value: "CUSTOM",
              },
            ]}
          />
          {scoreFilter?.kind === "custom" && (
            <Space size={4} align="start">
              <span>
                <InputNumber
                  style={{ width: 92 }}
                  // 不设 min/max/precision：保留用户原始输入（含 -1/1001/99.5），
                  // 由行内校验就地拦截，避免失焦被组件自动修正为合法值
                  placeholder="最低分"
                  value={scoreFilter.min}
                  onChange={(v) =>
                    setScoreFilter({ kind: "custom", min: v, max: scoreFilter.max })
                  }
                  status={customMinError ? "error" : undefined}
                  data-testid="score-custom-min"
                />
                {customMinError && (
                  <div
                    style={{ color: "#ff4d4f", fontSize: 12 }}
                    data-testid="score-custom-min-error"
                  >
                    {customMinError}
                  </div>
                )}
              </span>
              <Text type="secondary">-</Text>
              <span>
                <InputNumber
                  style={{ width: 92 }}
                  placeholder="最高分"
                  value={scoreFilter.max}
                  onChange={(v) =>
                    setScoreFilter({ kind: "custom", min: scoreFilter.min, max: v })
                  }
                  status={customMaxError ? "error" : undefined}
                  data-testid="score-custom-max"
                />
                {customMaxError && (
                  <div
                    style={{ color: "#ff4d4f", fontSize: 12 }}
                    data-testid="score-custom-max-error"
                  >
                    {customMaxError}
                  </div>
                )}
              </span>
            </Space>
          )}
          <Select
            placeholder="性别"
            allowClear
            style={{ width: 110 }}
            value={gender}
            onChange={(v) => {
              setGender(v);
              setQueried(false);
            }}
            options={Object.entries(genderLabel).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Button
            type="primary"
            loading={loading}
            onClick={() => void runQuery()}
          >
            查询
          </Button>
        </Space>
        {skuSummaries.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {skuSummaries.map((s) => (
              <Text key={s.skuId} type="secondary" style={{ fontSize: 12 }}>
                分班规则：{s.skuName} · 每班最多 {s.maxClassCapacity} 人 · 最多{" "}
                {s.maxClassCount} 个班（课程SKU中配置）
                <br />
              </Text>
            ))}
          </div>
        )}
      </div>

      {queryError && (
        <Alert
          type="error"
          showIcon
          message={queryError}
          style={{ marginBottom: 12 }}
        />
      )}

      {queried && !queryError && (
        <>
          <Space size={24} style={{ marginBottom: 12 }}>
            <Text>
              符合条件且未分班：<b>{totalCandidates}</b> 人
            </Text>
            <Text>
              建议班级数：<b>{groups.length}</b>
            </Text>
            {overflow.length > 0 && (
              <Text type="warning">
                超出最大班级数容量：<b>{overflow.length}</b> 人未纳入建议分组
              </Text>
            )}
          </Space>
          {notices.map((n) => (
            <Alert
              key={n}
              type="warning"
              showIcon
              message={n}
              style={{ marginBottom: 8 }}
            />
          ))}

          {groups.map((group) => {
            const results = groupResults[group.index] ?? [];
            const failCount = results.filter((r) => !r.success).length;
            return (
              <div
                className="page-content-card"
                key={group.index}
                style={{ marginBottom: 16 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <Space>
                    <Text strong>建议{group.index}班</Text>
                    {group.confirmed ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        成功 {results.length - failCount} 人
                        {failCount > 0 ? `，失败 ${failCount} 人` : ""}
                      </Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {group.students.length} 人
                        {(selected[group.index] ?? []).length > 0
                          ? `（已勾选 ${(selected[group.index] ?? []).length} 人，将入班）`
                          : "（未勾选，确认时不入班）"}
                      </Text>
                    )}
                    {group.confirmed && failCount === 0 && (
                      <Tag color="green">已确认入班</Tag>
                    )}
                    {group.confirmed && failCount > 0 && (
                      <Tag color="orange">部分失败</Tag>
                    )}
                  </Space>
                  {!group.confirmed && (
                    <Space size={4}>
                      <Button
                        size="small"
                        onClick={() => setAddingIndex(group.index)}
                      >
                        添加
                      </Button>
                      {hasUnconfirmedMoveTarget(groups, group.index) && (
                        <Button
                          size="small"
                          onClick={() => openMoveDialog(group)}
                        >
                          换班
                        </Button>
                      )}
                      {/* 最终确认（建班 + 批量入班）需 ADM-18 CREATE；越权由后端 403 兜底 */}
                      <HasPermission menuKey="ADM-18" action="CREATE">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => void openConfirmDialog(group)}
                        >
                          确认建班
                        </Button>
                      </HasPermission>
                    </Space>
                  )}
                  {group.confirmed && failCount > 0 && (
                    <HasPermission menuKey="ADM-18" action="CREATE">
                      <Button size="small" onClick={() => void retryGroup(group)}>
                        重新确认（补入失败学生）
                      </Button>
                    </HasPermission>
                  )}
                </div>
                <Table
                  scroll={{ x: 'max-content' }}
                  rowKey="studentId"
                  size="small"
                  dataSource={group.students}
                  pagination={false}
                  columns={groupColumns(group) as never}
                />
              </div>
            );
          })}

          {overflow.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${overflow.map((s) => s.name).join("、")} 超出该科目最多班级数限制，未纳入建议分组，可调整 SKU 配置后重新查询。`}
            />
          )}

          {groups.length === 0 && (
            <Alert
              type="info"
              showIcon
              message="当前条件下没有符合条件的未分班学生"
            />
          )}
        </>
      )}

      <DraggableModal
        open={confirming !== null}
        title="确认分班并创建班级"
        onCancel={() => {
          setConfirming(null);
          form.resetFields();
        }}
        destroyOnHidden
        width={560}
        footer={
          <Space>
            <Button
              onClick={() => {
                setConfirming(null);
                form.resetFields();
              }}
            >
              取消
            </Button>
            <Button type="primary" onClick={() => void submitConfirm()}>
              确认建班并入班
            </Button>
          </Space>
        }
      >
        <Space
          direction="vertical"
          size={4}
          style={{ width: "100%", marginBottom: 12 }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            系统已自动生成建班信息（名称序号、教师、教室、起止日期均为预填默认值），可修改后提交；提交后创建「招生中」班级并将当前勾选学生批量入班，取消勾选的学生不入班。
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            本次将入班：<b>{dialogStudents.length}</b> 名学生 · 课程SKU：
            {confirming?.preview.skuName} · 容量：
            {confirming?.preview.capacity} 人（SKU 每班最多人数）
          </Text>
          <div>
            {dialogStudents.map((s) => (
              <Tag key={s.studentId}>
                {s.name}（{genderLabel[s.gender] ?? s.gender}）
              </Tag>
            ))}
          </div>
        </Space>
        <Form form={form} layout="vertical">
          <Form.Item
            label="班级名称"
            name="className"
            rules={[{ required: true, message: "请输入班级名称" }]}
          >
            <Input placeholder="自动生成，可修改" />
          </Form.Item>
          <Space size={16} wrap>
            <Form.Item
              label="教师"
              name="teacherId"
              rules={[{ required: true, message: "请选择教师" }]}
            >
              <Select
                style={{ width: 180 }}
                placeholder="该校区教师"
                options={teacherSelectOptions}
              />
            </Form.Item>
            <Form.Item
              label="默认教室"
              name="classroomId"
              rules={[{ required: true, message: "请选择教室" }]}
            >
              <Select
                style={{ width: 200 }}
                placeholder="该校区可用教室"
                options={classrooms.map((r) => ({
                  label: `${r.name}（容量${r.capacity}）`,
                  value: r.id,
                }))}
              />
            </Form.Item>
          </Space>
          <Form.Item
            label="起止日期"
            name="dateRange"
            rules={[{ required: true, message: "请选择起止日期" }]}
          >
            <DatePicker.RangePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </DraggableModal>

      <DraggableModal
        open={addingIndex !== null}
        title={`添加学生至建议${addingIndex ?? 1}班`}
        onCancel={closeAddDialog}
        destroyOnHidden
        width={560}
        footer={
          <Space>
            <Button onClick={closeAddDialog}>取消</Button>
            <Button type="primary" onClick={submitAdd}>
              添加到该分组
            </Button>
          </Space>
        }
      >
        <Space
          direction="vertical"
          size={4}
          style={{ width: "100%", marginBottom: 12 }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            仅可搜索该科目下有剩余赠送或购买课时、且未在任何有效班级与当前分组中的学生；一次可添加多名，上限为每班最多人数；已在其他分组的学生添加后将自动移出原分组。
          </Text>
          {addingIndex !== null &&
            groups.find((g) => g.index === addingIndex) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                当前分组：
                {groups.find((g) => g.index === addingIndex)?.students.length ??
                  0}
                /{groups.find((g) => g.index === addingIndex)?.capacity ?? "-"}{" "}
                人
              </Text>
            )}
        </Space>
        <Select
          mode="multiple"
          showSearch
          filterOption={false}
          style={{ width: "100%" }}
          placeholder="输入姓名或学号搜索学生"
          value={addPicked}
          onSearch={setAddKeyword}
          onChange={setAddPicked}
          loading={addSearching}
          notFoundContent={addKeyword ? "无符合条件的学生" : "输入关键词搜索"}
          options={addCandidates.map((s) => {
            const inGroupIndex = groups.find(
              (g) =>
                g.index !== addingIndex &&
                g.students.some((x) => x.studentId === s.studentId),
            )?.index;
            return {
              label:
                `${s.name}（${s.studentNo}）· 剩余${s.remainingHours}课时` +
                (inGroupIndex !== undefined
                  ? ` · 已在建议${inGroupIndex}班`
                  : ""),
              value: s.studentId,
            };
          })}
        />
      </DraggableModal>

      <DraggableModal
        open={movingIndex !== null}
        title={`建议${movingIndex ?? 1}班学生换班`}
        onCancel={closeMoveDialog}
        destroyOnHidden
        width={480}
        footer={
          <Space>
            <Button onClick={closeMoveDialog}>取消</Button>
            <Button type="primary" onClick={submitMove}>
              确认换班
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            将把以下 <b>{movingStudents.length}</b>{" "}
            名学生移至目标分组；目标分组剩余容量须容纳全部换班学生。
          </Text>
          <div>
            {movingStudents.map((s) => (
              <Tag key={s.studentId}>
                {s.name}（{genderLabel[s.gender] ?? s.gender}）
              </Tag>
            ))}
          </div>
          <Select
            style={{ width: "100%" }}
            placeholder="选择目标分组"
            value={moveTarget}
            onChange={setMoveTarget}
            options={groups
              .filter((g) => g.index !== movingIndex && !g.confirmed)
              .map((g) => ({
                label: `建议${g.index}班 · ${g.students.length}/${g.capacity} 人`,
                value: g.index,
              }))}
          />
        </Space>
      </DraggableModal>
    </div>
  );
}
